// The balances table: one row per customer - opening balance, loads billed, invoices, payments, adjustments, ending balance, billing group totals.
// Part of the Balances page (components/Balances.jsx). Split out 2026-10-14.
import { fmtNum } from './helpers.js';
import DataTable from '../datatable/DataTable.jsx';
import { fmtMoneyRounded as fmtMoney } from '../../utils/money.js';

export default function BalancesTable({ billingRollup, canWrite, checks, childrenSet, customers, cycleKey, data, drill, from, load, loading, openAdjustmentsModal, openExplain, parentsSet, setDrill, to, totals }) {
  return (
    <>
      <DataTable
        viewName="balances.main"
        data={customers}
        emptyMessage={loading
          ? <div className="loading">Loading cycle {cycleKey || '…'}</div>
          : 'No activity in this period.'}
        defaultSort={[{ id: 'endingWithPending', desc: true }]}
        columns={[
          { id: 'customer', accessorKey: 'customer', header: 'Customer', size: 280,
            footer: () => 'TOTALS (' + customers.length + ' customers)',
            footerCheck: () => 'CHECKS (from source systems)',
            cell: ({ row, getValue }) => {
              const c = row.original;
              const name = getValue();
              const lname = String(name || '').toLowerCase();
              const isChild  = childrenSet.has(lname);
              const isParent = parentsSet.has(lname);
              const groupSize = isParent ? (billingRollup.find(g => g.displayName.toLowerCase() === lname) || { children: [] }).children.length : 0;
              return (
                <span>
                  {isChild && <span style={{ color: '#0369a1', marginRight: 4 }}>↳</span>}
                  <strong>{name}</strong>
                  {openExplain && (
                    <a href="#" className="no-print" title="Explain the difference: what changed in closed periods since they were saved"
                       onClick={(e) => { e.preventDefault(); openExplain(c.customer); }}
                       style={{ marginLeft: 6, fontSize: '0.75em', color: '#0369a1', whiteSpace: 'nowrap' }}>🔍 explain</a>
                  )}
                  {isChild && c.billedTo && (
                    <span style={{ marginLeft: 4, fontSize: '0.8em', color: '#0369a1', fontStyle: 'italic' }}>
                      (billed to {c.billedTo})
                    </span>
                  )}
                  {isParent && groupSize > 0 && (
                    <span style={{ marginLeft: 4, fontSize: '0.8em', color: '#15803d', fontStyle: 'italic' }}>
                      (+{groupSize} grouped)
                    </span>
                  )}
                </span>
              );
            } },
          { id: 'opening', accessorFn: r => r.opening || 0, header: 'Opening', size: 110,
            meta: { align: 'right' },
            footer: () => fmtMoney(totals.opening || 0),
            cell: ({ getValue }) => fmtMoney(getValue()) },
          { id: 'tipsCount',
            accessorFn: r => ((r.tips && r.tips.count) || 0) + ((r.pendingTips && r.pendingTips.count) || 0),
            header: 'Tips #', size: 120,
            meta: { align: 'right' },
            footer: () => fmtNum((totals.tipsCount || 0) + (totals.pendingCount || 0)),
            footerCheck: () => {
              const sheet = checks.sheetTotalCount || 0;
              const ours  = (totals.tipsCount || 0) + (totals.pendingCount || 0);
              const diff  = sheet - ours;
              return (
                <span title="Total rows in Google Sheet for this period">
                  Sheet: <strong>{fmtNum(sheet)}</strong>
                  {diff !== 0 && <span style={{ color: '#b91c1c', marginLeft: 4 }}>({diff > 0 ? '+' : ''}{diff})</span>}
                </span>
              );
            },
            cell: ({ row }) => {
              const c = row.original;
              const pend = c.pendingTips || { count: 0, rows: [] };
              const approvedCount = c.tips.count || 0;
              const combined = approvedCount + (pend.count || 0);
              if (combined === 0) return <span style={{ color: '#888' }}>0</span>;
              // Tag rows so the drill modal can group + label them
              const merged = [
                ...(c.tips.rows || []).map(r => ({ ...r, _pending: false })),
                ...(pend.rows || []).map(r => ({ ...r, _pending: true }))
              ];
              return (
                <span>
                  <a href="#" onClick={(e) => { e.preventDefault(); setDrill({ type: 'tips', customer: c.customer, rows: merged }); }} style={{ color: '#0369a1' }}>
                    {combined}
                  </a>
                  {pend.count > 0 && (
                    <span style={{ marginLeft: 4, fontSize: '0.8em', color: '#92400e', fontStyle: 'italic' }}>
                      ({pend.count} pending)
                    </span>
                  )}
                </span>
              );
            } },
          { id: 'size_lt10',
            accessorFn: r => ((r.tips && r.tips.bySize && r.tips.bySize['<10']) || 0) + ((r.pendingTips && r.pendingTips.bySize && r.pendingTips.bySize['<10']) || 0),
            header: '<10m³', size: 78, meta: { align: 'right' },
            cell: ({ row }) => {
              const c = row.original;
              const a = (c.tips && c.tips.bySize && c.tips.bySize['<10']) || 0;
              const p = (c.pendingTips && c.pendingTips.bySize && c.pendingTips.bySize['<10']) || 0;
              if (a + p === 0) return '';
              const av = (c.tips && c.tips.valueBySize && c.tips.valueBySize['<10']) || 0;
              const pv = (c.pendingTips && c.pendingTips.valueBySize && c.pendingTips.valueBySize['<10']) || 0;
              return (
                <span title={'$' + (av + pv).toLocaleString() + ' total' + (p > 0 ? ' · ' + p + ' pending' : '')}>
                  <strong>{a + p}</strong>
                  {p > 0 && <span style={{ color: '#92400e', fontStyle: 'italic', fontSize: '0.78em', marginLeft: 2 }}>+{p}p</span>}
                </span>
              );
            } },
          { id: 'size_10_17',
            accessorFn: r => ((r.tips && r.tips.bySize && r.tips.bySize['10-17']) || 0) + ((r.pendingTips && r.pendingTips.bySize && r.pendingTips.bySize['10-17']) || 0),
            header: '10-17m³', size: 78, meta: { align: 'right' },
            cell: ({ row }) => {
              const c = row.original;
              const a = (c.tips && c.tips.bySize && c.tips.bySize['10-17']) || 0;
              const p = (c.pendingTips && c.pendingTips.bySize && c.pendingTips.bySize['10-17']) || 0;
              if (a + p === 0) return '';
              const av = (c.tips && c.tips.valueBySize && c.tips.valueBySize['10-17']) || 0;
              const pv = (c.pendingTips && c.pendingTips.valueBySize && c.pendingTips.valueBySize['10-17']) || 0;
              return (
                <span title={'$' + (av + pv).toLocaleString() + ' total' + (p > 0 ? ' · ' + p + ' pending' : '')}>
                  <strong>{a + p}</strong>
                  {p > 0 && <span style={{ color: '#92400e', fontStyle: 'italic', fontSize: '0.78em', marginLeft: 2 }}>+{p}p</span>}
                </span>
              );
            } },
          { id: 'size_18_27',
            accessorFn: r => ((r.tips && r.tips.bySize && r.tips.bySize['18-27']) || 0) + ((r.pendingTips && r.pendingTips.bySize && r.pendingTips.bySize['18-27']) || 0),
            header: '18-27m³', size: 78, meta: { align: 'right' },
            cell: ({ row }) => {
              const c = row.original;
              const a = (c.tips && c.tips.bySize && c.tips.bySize['18-27']) || 0;
              const p = (c.pendingTips && c.pendingTips.bySize && c.pendingTips.bySize['18-27']) || 0;
              if (a + p === 0) return '';
              const av = (c.tips && c.tips.valueBySize && c.tips.valueBySize['18-27']) || 0;
              const pv = (c.pendingTips && c.pendingTips.valueBySize && c.pendingTips.valueBySize['18-27']) || 0;
              return (
                <span title={'$' + (av + pv).toLocaleString() + ' total' + (p > 0 ? ' · ' + p + ' pending' : '')}>
                  <strong>{a + p}</strong>
                  {p > 0 && <span style={{ color: '#92400e', fontStyle: 'italic', fontSize: '0.78em', marginLeft: 2 }}>+{p}p</span>}
                </span>
              );
            } },
          { id: 'size_28plus',
            accessorFn: r => ((r.tips && r.tips.bySize && r.tips.bySize['28+']) || 0) + ((r.pendingTips && r.pendingTips.bySize && r.pendingTips.bySize['28+']) || 0),
            header: '28+m³', size: 78, meta: { align: 'right' },
            cell: ({ row }) => {
              const c = row.original;
              const a = (c.tips && c.tips.bySize && c.tips.bySize['28+']) || 0;
              const p = (c.pendingTips && c.pendingTips.bySize && c.pendingTips.bySize['28+']) || 0;
              if (a + p === 0) return '';
              const av = (c.tips && c.tips.valueBySize && c.tips.valueBySize['28+']) || 0;
              const pv = (c.pendingTips && c.pendingTips.valueBySize && c.pendingTips.valueBySize['28+']) || 0;
              return (
                <span title={'$' + (av + pv).toLocaleString() + ' total' + (p > 0 ? ' · ' + p + ' pending' : '')}>
                  <strong>{a + p}</strong>
                  {p > 0 && <span style={{ color: '#92400e', fontStyle: 'italic', fontSize: '0.78em', marginLeft: 2 }}>+{p}p</span>}
                </span>
              );
            } },
          { id: 'size_unknown',
            accessorFn: r => ((r.tips && r.tips.bySize && r.tips.bySize['unknown']) || 0) + ((r.pendingTips && r.pendingTips.bySize && r.pendingTips.bySize['unknown']) || 0),
            header: '? m³', size: 78, meta: { align: 'right' },
            cell: ({ row }) => {
              const c = row.original;
              const a = (c.tips && c.tips.bySize && c.tips.bySize['unknown']) || 0;
              const p = (c.pendingTips && c.pendingTips.bySize && c.pendingTips.bySize['unknown']) || 0;
              if (a + p === 0) return '';
              const av = (c.tips && c.tips.valueBySize && c.tips.valueBySize['unknown']) || 0;
              const pv = (c.pendingTips && c.pendingTips.valueBySize && c.pendingTips.valueBySize['unknown']) || 0;
              return (
                <span title={'$' + (av + pv).toLocaleString() + ' total' + (p > 0 ? ' · ' + p + ' pending' : '')}>
                  <strong>{a + p}</strong>
                  {p > 0 && <span style={{ color: '#92400e', fontStyle: 'italic', fontSize: '0.78em', marginLeft: 2 }}>+{p}p</span>}
                </span>
              );
            } },
          // Drill mud subtotal — sums drillMudAmount from approved + pending.
          // Drill mud rows are already counted in totalValue; this column
          // breaks out the drill-mud portion so finance can see how much
          // of the total is drill-mud surcharge.
          { id: 'drillMudValue',
            accessorFn: r => ((r.tips && r.tips.drillMudAmount) || 0) + ((r.pendingTips && r.pendingTips.drillMudAmount) || 0),
            header: 'Drill mud $', size: 120,
            meta: { align: 'right' },
            cell: ({ row }) => {
              const c = row.original;
              const a  = (c.tips && c.tips.drillMudAmount) || 0;
              const ac = (c.tips && c.tips.drillMudCount)  || 0;
              const p  = (c.pendingTips && c.pendingTips.drillMudAmount) || 0;
              const pc = (c.pendingTips && c.pendingTips.drillMudCount)  || 0;
              if (a + p === 0) return '';
              return (
                <span title={(ac + pc) + ' drill mud load' + (ac + pc === 1 ? '' : 's')}>
                  {fmtMoney(a + p)}
                  {p > 0 && <span style={{ marginLeft: 4, fontSize: '0.8em', color: '#92400e', fontStyle: 'italic' }}>({fmtMoney(p)} pending)</span>}
                </span>
              );
            } },
          { id: 'tipsValue',
            accessorFn: r => ((r.tips && r.tips.totalValue) || 0) + ((r.pendingTips && r.pendingTips.totalValue) || 0),
            header: 'Tips $', size: 150,
            meta: { align: 'right' },
            footer: () => fmtMoney((totals.tipsValue || 0) + (totals.pendingValue || 0)),
            cell: ({ row }) => {
              const c = row.original;
              const pendVal = (c.pendingTips && c.pendingTips.totalValue) || 0;
              const tipsVal = (c.tips && c.tips.totalValue) || 0;
              return (
                <span>
                  {fmtMoney(tipsVal + pendVal)}
                  {pendVal > 0 && (
                    <span title="of which is pending"
                          style={{ marginLeft: 4, fontSize: '0.8em', color: '#92400e', fontStyle: 'italic' }}>
                      ({fmtMoney(pendVal)} pending)
                    </span>
                  )}
                </span>
              );
            } },
          { id: 'invoiceCount', accessorFn: r => (r.invoices && r.invoices.count) || 0, header: 'Invoices #', size: 90,
            meta: { align: 'right' },
            footer: () => fmtNum(totals.invoiceCount || 0),
            footerCheck: () => {
              const qb = checks.qbInvoiceCount || 0;
              const ours = totals.invoiceCount || 0;
              const diff = qb - ours;
              return (
                <span title="Total invoices in QuickBooks for this period (any line items)">
                  QB: <strong>{fmtNum(qb)}</strong>
                  {diff !== 0 && <span style={{ color: '#b91c1c', marginLeft: 4 }}>({diff > 0 ? '+' : ''}{diff})</span>}
                </span>
              );
            },
            cell: ({ row }) => {
              const c = row.original;
              return c.invoices.count > 0
                ? <a href="#" onClick={(e) => { e.preventDefault(); setDrill({ type: 'invoices', customer: c.customer, rows: c.invoices.list }); }} style={{ color: '#0369a1' }}>{c.invoices.count}</a>
                : '—';
            } },
          { id: 'disposal', accessorFn: r => (r.invoices && r.invoices.disposalFeesTotal) || 0, header: 'Disposal (B+N) $', size: 140,
            meta: { align: 'right' },
            footer: () => fmtMoney(totals.disposalFees || 0),
            footerCheck: () => {
              const qbTotal = checks.qbInvoiceTotal || 0;
              return (
                <span title="Pretax sum of every line item across all QB invoices in this period — matches disposal + other">
                  QB pretax: <strong>{fmtMoney(qbTotal)}</strong>
                </span>
              );
            },
            cell: ({ getValue }) => fmtMoney(getValue()) },
          { id: 'other', accessorFn: r => (r.invoices && r.invoices.otherTotal) || 0, header: 'Other $', size: 110,
            meta: { align: 'right' },
            footer: () => fmtMoney(totals.otherInvoice || 0),
            cell: ({ getValue }) => fmtMoney(getValue()) },
          { id: 'variance', accessorFn: r => r.variance || 0, header: 'Variance', size: 110,
            meta: { align: 'right' },
            footer: () => fmtMoney(totals.variance || 0),
            cell: ({ getValue }) => {
              const v = getValue();
              const varAbs = Math.abs(v || 0);
              const varColor = varAbs > 1 ? '#dc2626' : '#16a34a';
              return <span style={{ color: varColor, fontWeight: varAbs > 1 ? 600 : 400 }}>{fmtMoney(v)}</span>;
            } },
          { id: 'adjustments', accessorFn: r => (r.adjustments && r.adjustments.total) || 0, header: 'Adj $', size: 110,
            meta: { align: 'right', cellBg: '#f5f3ff' },
            footer: () => fmtMoney(totals.adjustmentsTotal || 0),
            cell: ({ row }) => {
              const c = row.original;
              const a = (c.adjustments && c.adjustments.total) || 0;
              const n = (c.adjustments && c.adjustments.items && c.adjustments.items.length) || 0;
              // Read-only users see the amount (or '—') without an edit link.
              if (!canWrite) {
                return <span style={{ color: a !== 0 ? '#7c3aed' : '#888', fontWeight: a !== 0 ? 600 : 400 }}>{a === 0 ? '—' : fmtMoney(a)}</span>;
              }
              return (
                <a href="#" onClick={(e) => { e.preventDefault(); openAdjustmentsModal(c.customer); }}
                   title={n + ' adjustment(s) — click to edit'}
                   style={{ color: a !== 0 ? '#7c3aed' : '#888', fontWeight: a !== 0 ? 600 : 400 }}>
                  {a === 0 ? '+ add' : fmtMoney(a)}
                </a>
              );
            } },
          { id: 'endingWithPending', accessorFn: r => (r.endingWithPending != null ? r.endingWithPending : r.ending) || 0, header: 'Ending', size: 130,
            meta: { align: 'right', cellBg: '#dbeafe' },
            footer: () => fmtMoney(totals.endingWithPending || 0),
            cell: ({ getValue }) => {
              const v = getValue();
              return <strong style={{ color: v > 0 ? '#dc2626' : '#1e40af' }}>{fmtMoney(v)}</strong>;
            } },
        ]}
      />
    </>
  );
}
